import React, { useEffect, useState } from 'react';
import { Lightbulb, X, Check, Sparkle } from '@phosphor-icons/react';
import Typhography from '../../../shared/Typhography/Typhography';
import CustomButton from '../../../shared/CustomButton/CustomButton';
import styles from './BlockClassification.module.scss';
import type {
  ISportTaxonomy,
  IDidacticClassification,
} from '../../../models/trainingStatistics';
import { useBlockClassificationSuggest } from './useBlockClassificationSuggest';

/**
 * Componente classificazione didattica blocco.
 * Spec: LEOPART_ProgrammazioneCampo_Spec_v1.1.1.md righe 2550-2668.
 *
 * UI:
 * - Dropdown fase seduta (filtrato per sport)
 * - Dropdown metodo didattico
 * - Multi-select obiettivi tecnici (max 3)
 * - Multi-select obiettivi tattici (max 3)
 * - Chip auto-suggest con bottoni Accetta/Rifiuta
 */
interface BlockClassificationProps {
  value: IDidacticClassification;
  onChange: (classification: IDidacticClassification) => void;
  taxonomy: ISportTaxonomy | null;
  sport: string;
  blockData?: {
    exerciseName?: string;
    tags?: string[];
    notes?: string;
    order?: number;
    durationMinutes?: number;
  };
  totalBlocks?: number;
  disabled?: boolean;
}

const BlockClassification: React.FC<BlockClassificationProps> = ({
  value,
  onChange,
  taxonomy,
  sport,
  blockData = {},
  totalBlocks,
  disabled = false,
}) => {
  const { suggestion, isLoading, error, fetchSuggestion, clearSuggestion, sendFeedback } =
    useBlockClassificationSuggest(blockData, sport, totalBlocks);

  const [showSuggestion, setShowSuggestion] = useState(false);

  useEffect(() => {
    if (suggestion) setShowSuggestion(true);
  }, [suggestion]);

  const phases = taxonomy?.sessionPhases ?? [];
  const methods = taxonomy?.didacticMethods ?? [];
  const techObjs = taxonomy?.technicalObjectives ?? [];
  const tactObjs = taxonomy?.tacticalObjectives ?? [];

  const handleAcceptSuggestion = async () => {
    if (!suggestion) return;
    const updated: IDidacticClassification = {
      ...value,
      sessionPhaseId: suggestion.sessionPhaseId ?? value.sessionPhaseId,
      didacticMethodId: suggestion.didacticMethodId ?? value.didacticMethodId,
      technicalObjectiveIds:
        suggestion.technicalObjectiveIds.length > 0
          ? suggestion.technicalObjectiveIds
          : value.technicalObjectiveIds,
      tacticalObjectiveIds:
        suggestion.tacticalObjectiveIds.length > 0
          ? suggestion.tacticalObjectiveIds
          : value.tacticalObjectiveIds,
      sessionPhaseSource: 'auto_accepted',
      didacticMethodSource: 'auto_accepted',
      technicalObjectivesSource: 'auto_accepted',
      tacticalObjectivesSource: 'auto_accepted',
    };
    onChange(updated);
    await sendFeedback(true, suggestion.sessionPhaseId ?? undefined, suggestion.didacticMethodId ?? undefined);
    setShowSuggestion(false);
    clearSuggestion();
  };

  const handleRejectSuggestion = async () => {
    await sendFeedback(false);
    setShowSuggestion(false);
    clearSuggestion();
  };

  const handlePhaseChange = (phaseId: string) => {
    onChange({ ...value, sessionPhaseId: phaseId || undefined, sessionPhaseSource: 'manual' });
  };

  const handleMethodChange = (methodId: string) => {
    onChange({ ...value, didacticMethodId: methodId || undefined, didacticMethodSource: 'manual' });
  };

  const toggleTechObjective = (objId: string) => {
    const current = value.technicalObjectiveIds ?? [];
    const updated = current.includes(objId)
      ? current.filter((id) => id !== objId)
      : current.length < 3
        ? [...current, objId]
        : current;
    onChange({ ...value, technicalObjectiveIds: updated, technicalObjectivesSource: 'manual' });
  };

  const toggleTactObjective = (objId: string) => {
    const current = value.tacticalObjectiveIds ?? [];
    const updated = current.includes(objId)
      ? current.filter((id) => id !== objId)
      : current.length < 3
        ? [...current, objId]
        : current;
    onChange({ ...value, tacticalObjectiveIds: updated, tacticalObjectivesSource: 'manual' });
  };

  const isPhaseAutoSuggested =
    suggestion && suggestion.sessionPhaseId && !value.sessionPhaseSource;
  const isMethodAutoSuggested =
    suggestion && suggestion.didacticMethodId && !value.didacticMethodSource;

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Sparkle className={styles.headerIcon} weight="fill" />
          <Typhography size="bold-14">Classificazione Didattica</Typhography>
          <span className={styles.optionalBadge}>opzionale</span>
        </div>
        <CustomButton
          size="xs"
          color="outline"
          label={isLoading ? 'Calcolo...' : 'Auto-suggest'}
          disabled={disabled || isLoading || !sport}
          onClick={fetchSuggestion}
          className={styles.suggestBtn}
        >
          <Lightbulb weight="fill" size={14} />
        </CustomButton>
      </div>

      {/* Suggestion banner */}
      {showSuggestion && suggestion && suggestion.confidence > 0 && (
        <div className={styles.suggestionBanner}>
          <div className={styles.suggestionHeader}>
            <Lightbulb className={styles.suggestionIcon} weight="fill" />
            <span className={styles.suggestionText}>
              Suggerimento automatico — confidenza{' '}
              {Math.round(suggestion.confidence * 100)}%
            </span>
          </div>
          <div className={styles.confidenceBar}>
            <div
              className={styles.confidenceFill}
              style={{ width: `${Math.round(suggestion.confidence * 100)}%` }}
            />
          </div>
          <div className={styles.suggestionActions}>
            <CustomButton
              size="xs"
              color="primary"
              label="Accetta"
              onClick={handleAcceptSuggestion}
            >
              <Check size={12} />
            </CustomButton>
            <CustomButton
              size="xs"
              color="outline"
              label="Rifiuta"
              onClick={handleRejectSuggestion}
            >
              <X size={12} />
            </CustomButton>
          </div>
        </div>
      )}

      {error && <span className={styles.errorText}>{error}</span>}

      {/* Fields grid */}
      <div className={styles.fieldsGrid}>
        {/* Fase seduta */}
        <div className={styles.fieldGroup}>
          <span className={styles.fieldLabel}>Fase seduta</span>
          <select
            className={`${styles.select} ${isPhaseAutoSuggested ? styles.selectAutoSuggested : ''}`}
            value={value.sessionPhaseId ?? ''}
            onChange={(e) => handlePhaseChange(e.target.value)}
            disabled={disabled}
          >
            <option value="">— Seleziona fase —</option>
            {phases.map((p) => (
              <option key={p.id} value={p.id}>
                {p.labelIt}
              </option>
            ))}
          </select>
        </div>

        {/* Metodo didattico */}
        <div className={styles.fieldGroup}>
          <span className={styles.fieldLabel}>Metodo didattico</span>
          <select
            className={`${styles.select} ${isMethodAutoSuggested ? styles.selectAutoSuggested : ''}`}
            value={value.didacticMethodId ?? ''}
            onChange={(e) => handleMethodChange(e.target.value)}
            disabled={disabled}
          >
            <option value="">— Seleziona metodo —</option>
            {methods.map((m) => (
              <option key={m.id} value={m.id}>
                {m.labelIt}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Obiettivi tecnici */}
      <div className={styles.fieldGroup}>
        <span className={styles.fieldLabel}>
          Obiettivi tecnici (max 3 — {value.technicalObjectiveIds?.length ?? 0}/3)
        </span>
        <div className={styles.tagRow}>
          {(value.technicalObjectiveIds ?? []).map((objId) => {
            const obj = techObjs.find((o) => o.id === objId);
            return (
              <span
                key={objId}
                className={`${styles.tag} ${suggestion?.technicalObjectiveIds.includes(objId) ? styles.tagAuto : ''}`}
                onClick={() => !disabled && toggleTechObjective(objId)}
              >
                {obj?.labelIt ?? objId}
                {!disabled && <X className={styles.tagRemove} weight="bold" size={12} />}
              </span>
            );
          })}
          {!disabled &&
            (value.technicalObjectiveIds?.length ?? 0) < 3 &&
            techObjs
              .filter((o) => !(value.technicalObjectiveIds ?? []).includes(o.id))
              .slice(0, 8)
              .map((obj) => (
                <span
                  key={obj.id}
                  className={`${styles.tag} ${styles.tagAdd}`}
                  onClick={() => toggleTechObjective(obj.id)}
                >
                  + {obj.labelIt}
                </span>
              ))}
        </div>
      </div>

      {/* Obiettivi tattici */}
      <div className={styles.fieldGroup}>
        <span className={styles.fieldLabel}>
          Obiettivi tattici (max 3 — {value.tacticalObjectiveIds?.length ?? 0}/3)
        </span>
        <div className={styles.tagRow}>
          {(value.tacticalObjectiveIds ?? []).map((objId) => {
            const obj = tactObjs.find((o) => o.id === objId);
            return (
              <span
                key={objId}
                className={`${styles.tag} ${suggestion?.tacticalObjectiveIds.includes(objId) ? styles.tagAuto : ''}`}
                onClick={() => !disabled && toggleTactObjective(objId)}
              >
                {obj?.labelIt ?? objId}
                {!disabled && <X className={styles.tagRemove} weight="bold" size={12} />}
              </span>
            );
          })}
          {!disabled &&
            (value.tacticalObjectiveIds?.length ?? 0) < 3 &&
            tactObjs
              .filter((o) => !(value.tacticalObjectiveIds ?? []).includes(o.id))
              .slice(0, 8)
              .map((obj) => (
                <span
                  key={obj.id}
                  className={`${styles.tag} ${styles.tagAdd}`}
                  onClick={() => toggleTactObjective(obj.id)}
                >
                  + {obj.labelIt}
                </span>
              ))}
        </div>
      </div>
    </div>
  );
};

export default BlockClassification;
